import React, { useState } from "react";
import AuthLayout from "../AuthLayout";
import styles from "./signup.module.scss";
import Button from "../../Shared/Button";
import EyeClose from "../../../assets/images/eye-close.svg";
import EyeOpen from "../../../assets/images/eye-open.svg";
import * as yup from "yup";
import { useFormik } from "formik";
import AuthAlerts from "./AuthAlerts";
import { SignUpUser, resendVerification } from "../../../helpers";
import { toast } from "react-toastify";
import AuthTextField from "../AuthTextField";
import CuteMascotSpinner from "../../../global/CuteMascotSpinner";

const SignupSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  bio: yup.string().required("Bio is required"),
  email: yup.string().email("Please enter valid email.").required("Required"),
  password: yup
    .string()
    .min(8, "Your password is not strong enough. Use at least 8 characters")
    .required("Required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Required"),
  phone: yup
    .string()
    .matches(/^[0-9]{10}$/, "Please enter valid phone number")
    .required("Required"),
});

const Signup = () => {
  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")?.[0];
  const isSeller = subdomain?.includes("seller");
  const [passwordToggle, setPasswordToggle] = useState(false);
  const [confirmPasswordToggle, setConfirmPasswordToggle] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const accountVerified = false;
  const [loading, setLoading] = useState(false);
  // const [accountVerified, setAccountVerified] = useState(false);
  const formik = useFormik({
    initialValues: {
      name: "",
      bio: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
    },
    validationSchema: SignupSchema,
    onSubmit: (values) => {
      handleSignup(values);
    },
  });
  const handleSignup = async (values) => {
    try {
      const params = {
        name: values?.name,
        email: values?.email,
        password: values?.password,
        confirm_password: values?.confirmPassword,
        bio: values?.bio,
        phone: values?.phone,
        role: isSeller ? "seller" : "admin",
      };
      setLoading(true);
      await SignUpUser(params);
      setLoading(false);
      toast.success("Account created successfully. Please verify your email");
      setAccountCreated(true);
      formik.setSubmitting(false);
    } catch (error) {
      setLoading(false);
      const errorMessage = error?.response?.data?.message || null;
      toast.error(
        errorMessage ? errorMessage : "Something went wrong. Please try again"
      );
      formik.setSubmitting(false);
    }
  };
  const handleResendVerification = async () => {
    const submitData = {
      email: formik?.values?.email,
    };
    try {
      const response = await resendVerification(submitData);
      toast.success(response?.message || "Email sent. Please check your inbox");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };
  const emailDomain = `https://${formik?.values?.email?.split("@")[1]}`;
  return (
    <AuthLayout hideSignupLabel>
      {loading && <CuteMascotSpinner />}
      <>
        {accountCreated || accountVerified ? (
          <>
            <AuthAlerts
              accountCreated={accountCreated}
              accountCreatedButNotVerified={accountVerified}
              buttonOnClick={() => {
                if (accountCreated) {
                  window.open(emailDomain, "_blank");
                }
              }}
              linkOnClick={() => {
                handleResendVerification();
              }}
            />
          </>
        ) : (
          <>
            <div className={styles["signup_container"]}>
              <div className={styles["signup_text"]}>Sign up</div>
              <div className={styles["email_field_container"]}>
                <div className={`${styles["name_field"]}`}>
                  <AuthTextField
                    heading="Name"
                    type="text"
                    onChange={formik.handleChange}
                    name="name"
                    error={formik?.errors?.name}
                    touched={formik?.touched?.name}
                    disabled={formik.isSubmitting}
                  />
                </div>
                <div className={`${styles["bio_field"]}`}>
                  <AuthTextField
                    heading="bio"
                    type="textarea"
                    rows={3}
                    onChange={formik.handleChange}
                    name="bio"
                    error={formik?.errors?.bio}
                    touched={formik?.touched?.bio}
                    disabled={formik.isSubmitting}
                  />
                </div>

                <AuthTextField
                  heading="Email"
                  type="email"
                  onChange={formik.handleChange}
                  name="email"
                  error={formik?.errors?.email}
                  touched={formik?.touched?.email}
                  disabled={formik.isSubmitting}
                />

                <div className={`${styles["phone_field"]}`}>
                  <AuthTextField
                    heading="Phone Number"
                    type="number"
                    onChange={formik.handleChange}
                    name="phone"
                    error={formik?.errors?.phone}
                    touched={formik?.touched?.phone}
                    disabled={formik.isSubmitting}
                  />
                </div>
                <div className={`${styles["password_field"]}`}>
                  <AuthTextField
                    heading="Password"
                    icon={passwordToggle ? EyeOpen : EyeClose}
                    iconOnClick={() => {
                      setPasswordToggle(!passwordToggle);
                    }}
                    type={passwordToggle ? "text" : "password"}
                    onChange={formik.handleChange}
                    name="password"
                    error={formik?.errors?.password}
                    touched={formik?.touched?.password}
                    disabled={formik.isSubmitting}
                  />
                </div>

                <AuthTextField
                  heading="Confirm password"
                  icon={confirmPasswordToggle ? EyeOpen : EyeClose}
                  iconOnClick={() => {
                    setConfirmPasswordToggle(!confirmPasswordToggle);
                  }}
                  type={confirmPasswordToggle ? "text" : "password"}
                  onChange={formik.handleChange}
                  name="confirmPassword"
                  error={formik?.errors?.confirmPassword}
                  touched={formik?.touched?.confirmPassword}
                  disabled={formik.isSubmitting}
                />
                <div className={styles["submit_button_container"]}>
                  <Button
                    type="submit"
                    className={styles["submit_button"]}
                    onClick={() => {
                      formik.submitForm();
                    }}
                    disabled={formik.isSubmitting}
                    text="Create account"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </>
    </AuthLayout>
  );
};

export default Signup;
